import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ColumnDef } from '@tanstack/react-table';
import { Eye, IdCard } from 'lucide-react';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { IconButton } from '@/shared/ui/buttons/IconButton';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { DataTable } from '@/shared/ui/data-table/DataTable';
import { Pagination } from '@/shared/ui/data-table/Pagination';
import { FilterBar } from '@/shared/ui/data-table/FilterBar';
import { CargoIdTypeBadge } from '@/shared/ui/chips/CargoIdTypeBadge';
import { extractErrorMessage } from '@/shared/api/errors';
import { useCargoIds } from '@/entities/cargo-id';
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue';
import { formatDate } from '@/shared/utils/date';
import { formatUsd } from '@/shared/utils/currency';
import type { ApiCargoIdGetItem } from '@/shared/types/api/cargo-id';

const PAGE_SIZE = 20;

export const WidgetsCargoIdsMain = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 350);

  const params = useMemo(
    () => ({
      page,
      page_size: PAGE_SIZE,
      search: debouncedSearch.trim() || undefined,
    }),
    [page, debouncedSearch],
  );

  const query = useCargoIds(params);

  const columns = useMemo<ColumnDef<ApiCargoIdGetItem>[]>(
    () => [
      {
        accessorKey: 'code',
        header: t('cargoIds.list.columns.code'),
        cell: (info) => (
          <span className="font-mono text-xs text-ink">{info.row.original.code ?? '—'}</span>
        ),
      },
      {
        accessorKey: 'entity_type',
        header: t('cargoIds.list.columns.type'),
        cell: (info) => <CargoIdTypeBadge type={info.row.original.entity_type} />,
      },
      {
        accessorKey: 'full_name',
        header: t('cargoIds.list.columns.fullName'),
        cell: (info) => {
          const name = info.row.original.full_name;
          return (
            <span className="font-medium text-ink">
              {name && name.trim() ? name : '—'}
            </span>
          );
        },
      },
      {
        accessorKey: 'phone_number',
        header: t('cargoIds.list.columns.phone'),
        cell: (info) => {
          const phone = info.row.original.phone_number;
          return phone && phone.trim() ? (
            <span className="font-mono text-sm text-ink">{phone}</span>
          ) : (
            <span className="text-ink-muted">—</span>
          );
        },
      },
      {
        accessorKey: 'parcels_count',
        header: t('cargoIds.list.columns.parcelsCount'),
        cell: (info) => <span className="font-medium">{info.row.original.parcels_count}</span>,
      },
      {
        accessorKey: 'debt',
        header: t('cargoIds.list.columns.debt'),
        cell: (info) => {
          const value = Number(info.row.original.debt ?? '0');
          return value > 0 ? (
            <span className="font-semibold text-amber-600">{formatUsd(value)}</span>
          ) : (
            <span className="text-ink-muted">{formatUsd(0)}</span>
          );
        },
      },
      {
        accessorKey: 'created_at',
        header: t('cargoIds.list.columns.createdAt'),
        cell: (info) => formatDate(info.row.original.created_at),
      },
      {
        id: 'actions',
        header: t('cargoIds.list.columns.actions'),
        enableSorting: false,
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="flex items-center gap-1.5">
              <IconButton
                label={t('cargoIds.list.actions.view')}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/cargo-ids/${row.id}`);
                }}
              >
                <Eye className="h-4 w-4" />
              </IconButton>
            </div>
          );
        },
      },
    ],
    [navigate, t],
  );

  return (
    <>
      <PageHeader title={t('cargoIds.list.title')} subtitle={t('cargoIds.list.subtitle')} />
      <div className="space-y-4 px-4 py-4 lg:px-6 lg:py-6">
        <FilterBar
          searchValue={search}
          onSearchChange={(v) => {
            setPage(1);
            setSearch(v);
          }}
          searchPlaceholder={t('cargoIds.list.searchPlaceholder')}
        />

        {query.error ? (
          <ErrorState
            message={extractErrorMessage(query.error, t('cargoIds.list.loadError'))}
            onRetry={() => void query.refetch()}
          />
        ) : (
          <>
            <DataTable
              data={query.data?.results ?? []}
              columns={columns}
              isLoading={query.isLoading}
              emptyTitle={t('cargoIds.list.emptyTitle')}
              emptyDescription={t('cargoIds.list.emptyDescription')}
              emptyIcon={<IdCard className="h-8 w-8" aria-hidden />}
              onRowClick={(row) => navigate(`/cargo-ids/${row.id}`)}
              getRowId={(row) => row.id}
              numbered
              pageIndex={page - 1}
              pageSize={PAGE_SIZE}
            />
            <Pagination
              page={page}
              pageSize={PAGE_SIZE}
              total={query.data?.count ?? 0}
              onPageChange={setPage}
            />
          </>
        )}
      </div>
    </>
  );
};
